import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Activity, MessageSquare, CheckCircle, BarChart3, Calendar, Users } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface ActivityItem {
  id: string;
  user_id: string;
  activity_type: string;
  metadata: any;
  created_at: string;
  profiles?: {
    username: string | null;
  };
}

const activityIcons = {
  course_completed: CheckCircle,
  discussion_posted: MessageSquare,
  reply_posted: MessageSquare,
  poll_created: BarChart3,
  poll_voted: BarChart3,
  event_registered: Calendar,
  mentor_match: Users
};

const activityLabels = {
  course_completed: "completed a course module",
  discussion_posted: "started a discussion",
  reply_posted: "replied to a discussion",
  poll_created: "created a poll",
  poll_voted: "voted on a poll",
  event_registered: "registered for an event",
  mentor_match: "requested mentorship"
};

export const ActivityFeed = () => {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivities();

    // Subscribe to new activities
    const channel = supabase
      .channel('activity-feed')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activity_feed'
        },
        () => {
          loadActivities();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadActivities = async () => {
    const { data: activitiesData } = await supabase
      .from("activity_feed")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(20);

    if (activitiesData) {
      const userIds = [...new Set(activitiesData.map(a => a.user_id))];
      const { data: profilesData } = await supabase
        .from("profiles")
        .select("id, username")
        .in("id", userIds);

      const profileMap = new Map(profilesData?.map(p => [p.id, p]) || []);

      const enrichedActivities = activitiesData.map(activity => ({
        ...activity,
        profiles: profileMap.get(activity.user_id)
      }));

      setActivities(enrichedActivities);
    }

    setLoading(false);
  };

  if (loading) {
    return <div className="text-center py-8">Loading activity feed...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Activity Feed</h2>
        <p className="text-muted-foreground">See what's happening in the community</p>
      </div>

      <div className="space-y-3">
        {activities.map((activity) => {
          const Icon = activityIcons[activity.activity_type as keyof typeof activityIcons] || Activity;
          const label = activityLabels[activity.activity_type as keyof typeof activityLabels] || "did something";

          return (
            <Card key={activity.id} className="hover:border-primary/50 transition-colors">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Icon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium">
                        {activity.profiles?.username || "Someone"}
                      </span>
                      {" "}
                      <span className="text-muted-foreground">{label}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {activities.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No Activity Yet</h3>
            <p className="text-muted-foreground">Be the first to get active in the community!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};